import React from 'react'

import {
  AlignItems,
  Box,
  Color,
  Display,
  FontWeight,
  Icon,
  IconVariant,
  Link,
  Text,
} from '@gousto-internal/citrus-react'
import classnames from 'classnames'

import { SaveButton } from 'components/OrderSummary/SaveButton'
import Overlay from 'components/Overlay'
import CloseButton from 'components/Overlay/CloseButton'
import { SidesModal } from 'routes/Menu/components/Sides/SidesModal'

import { GoustoAnytimeBanner } from '../../experiments/GoustoAnytime'
import type { Basket } from '../../types/basket'
import type { NavigationCategories } from '../../types/navigationCategory'
import type { ProductCategories } from '../../types/productCategory'
import type { Products } from '../../types/products'
import { OrderFeedback } from '../OrderFeedback/OrderFeedback'
import { OrderSummaryContainer } from '../OrderSummary/OrderSummaryContainer'
import { ProductList } from '../ProductList'
import { ProductsNavBar } from '../ProductsNavBar'
import { ReferAFriend } from '../ReferAFriend'

import css from './Market.css'

type Props = {
  ageVerified: boolean
  canEditOrder: boolean
  basket: Basket
  categoriesForNavBar: NavigationCategories
  filteredProducts: Products | null
  getFilteredProducts: (arg: string) => void
  isOrderSummaryOpen: boolean
  onOrderSave: () => void
  onRetryMarketLoad: () => void
  orderId: string
  products: Products
  productsCategories: ProductCategories
  productsLoadError: boolean
  showCategoryTags: boolean
  saveError: boolean
  saveRequired: boolean
  saving: boolean
  showGoustoAnytimeBanner: boolean
  showOrderConfirmationReceipt: boolean
  showContextualSidesModal: boolean
  toggleAgeVerificationPopUp: () => void
  toggleOrderSummary: () => void
  trackingCategory: string
}

const MarketPresentation = ({
  ageVerified = false,
  canEditOrder,
  basket,
  categoriesForNavBar,
  filteredProducts = null,
  getFilteredProducts,
  isOrderSummaryOpen = false,
  onOrderSave,
  onRetryMarketLoad,
  orderId,
  products,
  productsCategories,
  productsLoadError,
  showCategoryTags,
  saveError = false,
  saveRequired = false,
  saving = false,
  showGoustoAnytimeBanner,
  showOrderConfirmationReceipt,
  showContextualSidesModal,
  toggleAgeVerificationPopUp,
  toggleOrderSummary,
  trackingCategory = '',
}: Props) => (
  <div className={css.marketPlaceWrapper} data-testid="MarketPresentation">
    {!productsLoadError && (
      <ProductsNavBar categories={categoriesForNavBar} onSelectCategory={getFilteredProducts} />
    )}
    <div className={css.marketPlaceContent}>
      <section
        className={classnames(css.marketPlaceProducts, {
          [css.productsLoadError]: productsLoadError,
        })}
      >
        {productsLoadError && (
          <Box
            bg={Color.Warning_50}
            borderRadius={1}
            display={Display.InlineFlex}
            alignItems={AlignItems.FlexStart}
            gap={2}
            paddingH={2}
            paddingV={2}
            role="alert"
            aria-live="assertive"
            aria-atomic="true"
            width="100%"
          >
            <Box color={Color.Warning_700} display={Display.Flex}>
              <Icon name="warning" size={6} variant={IconVariant.Inherit} />
            </Box>
            <Text size={4}>
              We&apos;re sorry, Gousto Market is not available right now, please{' '}
              <Link
                onClick={(event) => {
                  event.preventDefault()
                  onRetryMarketLoad()
                }}
                style={{ textDecoration: 'none', boxShadow: 'none' }}
              >
                <Text fontWeight={FontWeight.Bold} size={4}>
                  try again
                </Text>
              </Link>
              .
            </Text>
          </Box>
        )}
        {!productsLoadError && (
          <ProductList
            canEditOrder={canEditOrder}
            products={filteredProducts || products}
            basket={basket}
            ageVerified={ageVerified}
            productsCategories={productsCategories}
            showCategoryTags={showCategoryTags}
            toggleAgeVerificationPopUp={toggleAgeVerificationPopUp}
            trackingCategory={trackingCategory}
          />
        )}
      </section>
      {showOrderConfirmationReceipt && (
        <section className={classnames(css.orderDetails, css.mobileHide)}>
          <div className={css.orderDetailsSection}>
            <OrderSummaryContainer canEditOrder={canEditOrder} onOrderConfirmationMobile />
          </div>
          {showGoustoAnytimeBanner && (
            <div className={css.orderDetailsSection}>
              <GoustoAnytimeBanner />
            </div>
          )}
          <div className={css.orderDetailsSection}>
            <ReferAFriend />
          </div>
          <div className={css.orderDetailsSection}>
            <OrderFeedback orderId={orderId} />
          </div>
        </section>
      )}
      <section className={classnames(css.orderDetailsMobile, css.mobileShow)}>
        <button className={css.orderDetailsOpenButton} type="button" onClick={toggleOrderSummary}>
          Open Order Summary
        </button>
        <Overlay open={isOrderSummaryOpen} from="bottom">
          <div className={css.orderDetailsMobileContent}>
            <div className={css.orderDetailsCloseButton}>
              <CloseButton onClose={toggleOrderSummary} />
            </div>
            <OrderSummaryContainer
              canEditOrder={canEditOrder}
              orderSummaryCollapsed={false}
              onOrderConfirmationMobile
            />
            {showGoustoAnytimeBanner && <GoustoAnytimeBanner withBorder />}
          </div>
        </Overlay>
        {showContextualSidesModal && <SidesModal />}
        <SaveButton
          disabled={!canEditOrder}
          onOrderConfirmationMobile
          saving={saving}
          saveRequired={saveRequired}
          onClick={onOrderSave}
          error={saveError}
        />
      </section>
    </div>
  </div>
)

export { MarketPresentation }
